import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { Button } from '~/components/ui/button';
import type { DiagramAreaPartsFragment } from '~/apollo/generated/v3/graphql';
import { DiagramParent } from '~/apollo/generated/v3/graphql';
import { Heading } from '~/components/common/Heading';
import { Panel } from '~/components/common/Panel';
import { DiagramRenderer } from '~/components/diagram/DiagramRenderer';
import { DeleteDiagram } from '~/components/upload/diagram/DeleteDiagram';
import { DiagramEditor } from '~/components/upload/diagram/DiagramEditor';
import { UploadDiagramImageForm } from '~/components/upload/diagram/UploadDiagramImageForm';
import { useLithostratTypeUpdateOutletContext } from '~/routes/upload/lithostrat-type/$lithostratTypeId';

type DiagramAreaId = DiagramAreaPartsFragment['id'];

export default function LithostratTypeUpdateDiagramRoute() {
  const { lithostratType, refetchQueries } =
    useLithostratTypeUpdateOutletContext();

  const { diagram } = lithostratType;
  const [hoveredAreaId, setHoveredAreaId] = useState<DiagramAreaId>();

  function handleHover(areaId?: DiagramAreaId) {
    setHoveredAreaId(areaId);
  }

  if (!diagram) {
    return (
      <div className="lg:w-1/2 mx-auto">
        <Heading level={3}>Create a Diagram</Heading>
        <p>
          This lithostrat type doesn't have a diagram yet. Get started by
          uploading one below.
        </p>

        <UploadDiagramImageForm
          parentType={DiagramParent.LithostratType}
          parentId={lithostratType.id}
          refetchQueries={refetchQueries}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DiagramEditor
        diagram={diagram}
        onHover={handleHover}
        hoveredAreaId={hoveredAreaId}
      />

      <div className="text-center">
        <DeleteDiagram diagramId={diagram.id} refetchQueries={refetchQueries}>
          {confirmDelete => (
            <Button
              type="button"
              variant="link"
              onClick={confirmDelete}
              className="text-error hover:text-error gap-1"
            >
              <FontAwesomeIcon icon={faTrash} /> Delete Diagram
            </Button>
          )}
        </DeleteDiagram>
      </div>

      <Panel>
        <Panel.Heading>
          <Panel.Title>Preview</Panel.Title>
        </Panel.Heading>
        <Panel.Body className="text-center">
          <DiagramRenderer diagram={diagram} />
        </Panel.Body>
      </Panel>
    </div>
  );
}
